// UI Feedback Helper Component
// SCHRITT 2 & 5: Sofortiges Feedback und Match-Reasons Visualisierung

import React from 'react';
import { Check, MapPin, Plane, Sun, Calendar, Users, Sparkles, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t, translateMonth } from '../i18n/translations';
import '../styles/FeedbackChips.css';

export const SelectionChips = ({ selectedAnswers, questions }) => {
  const { language } = useLanguage();
  
  const getIcon = (questionId) => {
    const iconMap = {
      'q1': <Calendar size={14} />,
      'q2': <Plane size={14} />,
      'q4': <Sun size={14} />,
      'q5': <MapPin size={14} />,
      'q6': <Users size={14} />,
      'q7': <Clock size={14} />,
      'q8': <Sparkles size={14} />,
      'q11': <Sparkles size={14} />
    };
    return iconMap[questionId] || null;
  };

  const getLabel = (questionId) => {
    const labels = {
      de: {
        'q1': 'Saison',
        'q2': 'Entfernung',
        'q4': 'Klima',
        'q5': 'Szenario',
        'q6': 'Gäste',
        'q7': 'Dauer',
        'q8': 'Budget',
        'q11': 'Ambiente'
      },
      en: {
        'q1': 'Season',
        'q2': 'Distance',
        'q4': 'Climate',
        'q5': 'Scenario',
        'q6': 'Guests',
        'q7': 'Duration',
        'q8': 'Budget',
        'q11': 'Ambiance'
      },
      es: {
        'q1': 'Temporada',
        'q2': 'Distancia',
        'q4': 'Clima',
        'q5': 'Escenario',
        'q6': 'Invitados',
        'q7': 'Duración',
        'q8': 'Presupuesto',
        'q11': 'Ambiente'
      }
    };
    return labels[language]?.[questionId] || labels['de'][questionId];
  };
  
  // Formatiere Display-Werte für bessere Lesbarkeit
  // Unterstützt sowohl lokale Konstanten (deutsch) als auch Datenbank-Values (englisch)
  const formatDisplayValue = (questionId, value) => {
    // Saison: Zeige Monatsnamen
    if (questionId === 'q1') {
      const seasonMap = {
        de: {
          // Datenbankformat (neu)
          'jan_feb': 'Januar - Februar',
          'mar_apr': 'März - April',
          'mai_jun': 'Mai - Juni',
          'jul_aug': 'Juli - August',
          'sep_okt': 'September - Oktober',
          'nov_dez': 'November - Dezember',
          // Originalformat
          'Januar-Februar': 'Januar - Februar',
          'März-April': 'März - April',
          'Mai-Juni': 'Mai - Juni',
          'Juli-August': 'Juli - August',
          'September-Oktober': 'September - Oktober',
          'November-Dezember': 'November - Dezember'
        },
        en: {
          'jan_feb': 'January - February',
          'mar_apr': 'March - April',
          'mai_jun': 'May - June',
          'jul_aug': 'July - August',
          'sep_okt': 'September - October',
          'nov_dez': 'November - December',
          'Januar-Februar': 'January - February',
          'März-April': 'March - April',
          'Mai-Juni': 'May - June',
          'Juli-August': 'July - August',
          'September-Oktober': 'September - October',
          'November-Dezember': 'November - December'
        },
        es: {
          'jan_feb': 'Enero - Febrero',
          'mar_apr': 'Marzo - Abril',
          'mai_jun': 'Mayo - Junio',
          'jul_aug': 'Julio - Agosto',
          'sep_okt': 'Septiembre - Octubre',
          'nov_dez': 'Noviembre - Diciembre',
          'Januar-Februar': 'Enero - Febrero',
          'März-April': 'Marzo - Abril',
          'Mai-Juni': 'Mayo - Junio',
          'Juli-August': 'Julio - Agosto',
          'September-Oktober': 'Septiembre - Octubre',
          'November-Dezember': 'Noviembre - Diciembre'
        }
      };
      return seasonMap[language]?.[value] || seasonMap['de'][value] || value;
    }
    
    // Entfernung: Zeige Frontend-Text statt Zahl
    if (questionId === 'q2') {
      const distanceMap = {
        de: {
          '1': 'Nah (Europa)',
          '2': 'Mittel (Nordafrika, Naher Osten)',
          '3': 'Fern (Asien, Afrika)',
          '4': 'Weit (Amerika, Ozeanien)'
        },
        en: {
          '1': 'Near (Europe)',
          '2': 'Medium (North Africa, Middle East)',
          '3': 'Far (Asia, Africa)',
          '4': 'Very Far (America, Oceania)'
        },
        es: {
          '1': 'Cerca (Europa)',
          '2': 'Medio (Norte de África, Oriente Medio)',
          '3': 'Lejos (Asia, África)',
          '4': 'Muy Lejos (América, Oceanía)'
        }
      };
      return distanceMap[language]?.[value] || distanceMap['de'][value] || value;
    }
    
    // Gäste: Formatierung
    if (questionId === 'q6') {
      const guestMap = {
        de: {
          'zu_zweit': 'Nur wir zwei',
          'ca_15': 'Ca. 15 Gäste',
          'ca_25': 'Ca. 25 Gäste',
          'ca_40': 'Ca. 40 Gäste',
          'mehr_40': 'Mehr als 40'
        },
        en: {
          'zu_zweit': 'Just us two',
          'ca_15': 'About 15 guests',
          'ca_25': 'About 25 guests',
          'ca_40': 'About 40 guests',
          'mehr_40': 'More than 40'
        },
        es: {
          'zu_zweit': 'Solo nosotros',
          'ca_15': 'Aprox. 15 invitados',
          'ca_25': 'Aprox. 25 invitados',
          'ca_40': 'Aprox. 40 invitados',
          'mehr_40': 'Más de 40'
        }
      };
      return guestMap[language]?.[value] || guestMap['de'][value] || value;
    }
    
    // Budget: Formatierung
    if (questionId === 'q8') {
      const budgetMap = {
        de: {
          'bis_25k': 'Bis 25.000€',
          '20_40k': '20.000€ - 40.000€',
          '25_40k': '25.001€ - 40.000€',
          '40_60k': '40.001€ - 60.000€',
          '60_90k': '60.001€ - 90.000€',
          '100k_plus': '100.000€+'
        },
        en: {
          'bis_25k': 'Up to €25,000',
          '20_40k': '€20,000 - €40,000',
          '25_40k': '€25,001 - €40,000',
          '40_60k': '€40,001 - €60,000',
          '60_90k': '€60,001 - €90,000',
          '100k_plus': '€100,000+'
        },
        es: {
          'bis_25k': 'Hasta €25,000',
          '20_40k': '€20,000 - €40,000',
          '25_40k': '€25,001 - €40,000',
          '40_60k': '€40,001 - €60,000',
          '60_90k': '€60,001 - €90,000',
          '100k_plus': '€100,000+'
        }
      };
      return budgetMap[language]?.[value] || budgetMap['de'][value] || value;
    }
    
    // Klima: Formatierung - unterstützt DB-Values (english) und lokale Konstanten (deutsch)
    if (questionId === 'q4') {
      const climateMap = {
        de: {
          // DB Values (english)
          'tropical': 'Tropisch & Warm',
          'mediterranean': 'Mediterran & Mild',
          'temperate': 'Gemäßigt & Kühl',
          'dry': 'Trocken & Sonnig',
          'alpine': 'Alpin & Polar',
          // Lokale Konstanten (deutsch)
          'tropisch & warm': 'Tropisch & Warm',
          'mediterran & mild': 'Mediterran & Mild',
          'gemäßigt & kühl': 'Gemäßigt & Kühl',
          'trocken & sonnig': 'Trocken & Sonnig',
          'alpin & polar': 'Alpin & Polar'
        },
        en: {
          'tropical': 'Tropical & Warm',
          'mediterranean': 'Mediterranean & Mild',
          'temperate': 'Temperate & Cool',
          'dry': 'Dry & Sunny',
          'alpine': 'Alpine & Polar',
          'tropisch & warm': 'Tropical & Warm',
          'mediterran & mild': 'Mediterranean & Mild',
          'gemäßigt & kühl': 'Temperate & Cool',
          'trocken & sonnig': 'Dry & Sunny',
          'alpin & polar': 'Alpine & Polar'
        },
        es: {
          'tropical': 'Tropical y Cálido',
          'mediterranean': 'Mediterráneo y Suave',
          'temperate': 'Templado y Fresco',
          'dry': 'Seco y Soleado',
          'alpine': 'Alpino y Polar',
          'tropisch & warm': 'Tropical y Cálido',
          'mediterran & mild': 'Mediterráneo y Suave',
          'gemäßigt & kühl': 'Templado y Fresco',
          'trocken & sonnig': 'Seco y Soleado',
          'alpin & polar': 'Alpino y Polar'
        }
      };
      return climateMap[language]?.[value] || climateMap['de'][value] || value;
    }
    
    // Szenario: Formatierung - unterstützt DB-Values (english) und lokale Konstanten (deutsch)
    if (questionId === 'q5') {
      const scenarioMap = {
        de: {
          // DB Values (english)
          'beach': 'Strand',
          'rainforest': 'Regenwald',
          'mountains': 'Berge',
          'city': 'City',
          'desert': 'Wüste',
          // Lokale Konstanten (deutsch)
          'Strand': 'Strand',
          'Regenwald': 'Regenwald',
          'Berge': 'Berge',
          'City': 'City',
          'Wüste': 'Wüste'
        },
        en: {
          'beach': 'Beach',
          'rainforest': 'Rainforest',
          'mountains': 'Mountains',
          'city': 'City',
          'desert': 'Desert',
          'Strand': 'Beach',
          'Regenwald': 'Rainforest',
          'Berge': 'Mountains',
          'City': 'City',
          'Wüste': 'Desert'
        },
        es: {
          'beach': 'Playa',
          'rainforest': 'Selva',
          'mountains': 'Montañas',
          'city': 'Ciudad',
          'desert': 'Desierto',
          'Strand': 'Playa',
          'Regenwald': 'Selva',
          'Berge': 'Montañas',
          'City': 'Ciudad',
          'Wüste': 'Desierto'
        }
      };
      return scenarioMap[language]?.[value] || scenarioMap['de'][value] || value;
    }
    
    // Dauer (Anzahl der Tage): Formatierung
    if (questionId === 'q7') {
      const daysMap = {
        de: {
          '1': '1 Tag',
          '2': '2 Tage',
          '3': '3 Tage',
          '4': '4 Tage'
        },
        en: {
          '1': '1 Day',
          '2': '2 Days',
          '3': '3 Days',
          '4': '4 Days'
        },
        es: {
          '1': '1 Día',
          '2': '2 Días',
          '3': '3 Días',
          '4': '4 Días'
        }
      };
      return daysMap[language]?.[value] || daysMap['de'][value] || `${value} ${language === 'de' ? 'Tage' : language === 'en' ? 'Days' : 'Días'}`;
    }
    
    return value;
  };

  const getSelectionTitle = () => {
    const titles = {
      de: 'Ihre Auswahl:',
      en: 'Your Selection:',
      es: 'Tu Selección:'
    };
    return titles[language] || titles['de'];
  };

  // Versuche zuerst das Label direkt aus den Fragen-Daten zu holen (robuster als hardcodierte Maps)
  const getAnswerDisplay = (qId, value) => {
    if (questions) {
      const question = questions.find(q => q.id === qId);
      if (question && question.options) {
        const option = question.options.find(o => o.value === value);
        if (option) {
          const label = language === 'de' ? option.label_de 
                      : language === 'en' ? option.label_en 
                      : language === 'es' ? option.label_es 
                      : option.label_de || option.label;
          if (label) return label;
        }
      }
    }
    return formatDisplayValue(qId, value);
  };

  const relevantQuestions = ['q1', 'q2', 'q4', 'q5', 'q6', 'q7', 'q8'];
  
  return (
    <div className="selection-chips-container">
      <p className="chips-title">{getSelectionTitle()}</p>
      <div className="chips-grid">
        {relevantQuestions.map(qId => {
          const answer = selectedAnswers[qId];
          if (!answer) return null;
          
          return (
            <div key={qId} className="selection-chip">
              {getIcon(qId)}
              <div className="chip-content">
                <span className="chip-label">{getLabel(qId)}</span>
                <span className="chip-value">{getAnswerDisplay(qId, answer)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MatchReasonsDisplay = ({ reasons }) => {
  const { language } = useLanguage();
  
  const reasonConfig = {
    de: {
      'season': { label: 'Passende Saison', icon: <Calendar size={16} /> },
      'distance': { label: 'Ideale Entfernung', icon: <Plane size={16} /> },
      'climate': { label: 'Perfektes Klima', icon: <Sun size={16} /> },
      'scenario': { label: 'Gewünschtes Szenario', icon: <MapPin size={16} /> },
      'culture': { label: 'Kulturelle Präferenz', icon: <Sparkles size={16} /> },
      'ambiance': { label: 'Passendes Ambiente', icon: <Sparkles size={16} /> }
    },
    en: {
      'season': { label: 'Matching Season', icon: <Calendar size={16} /> },
      'distance': { label: 'Ideal Distance', icon: <Plane size={16} /> },
      'climate': { label: 'Perfect Climate', icon: <Sun size={16} /> },
      'scenario': { label: 'Desired Scenario', icon: <MapPin size={16} /> },
      'culture': { label: 'Cultural Preference', icon: <Sparkles size={16} /> },
      'ambiance': { label: 'Suitable Ambiance', icon: <Sparkles size={16} /> }
    },
    es: {
      'season': { label: 'Temporada Adecuada', icon: <Calendar size={16} /> },
      'distance': { label: 'Distancia Ideal', icon: <Plane size={16} /> },
      'climate': { label: 'Clima Perfecto', icon: <Sun size={16} /> },
      'scenario': { label: 'Escenario Deseado', icon: <MapPin size={16} /> },
      'culture': { label: 'Preferencia Cultural', icon: <Sparkles size={16} /> },
      'ambiance': { label: 'Ambiente Adecuado', icon: <Sparkles size={16} /> }
    }
  };

  const configForLang = reasonConfig[language] || reasonConfig['de'];

  return (
    <div className="match-reasons-container">
      {reasons.map((reason, idx) => {
        const config = configForLang[reason];
        if (!config) return null;
        
        return (
          <div key={idx} className="match-reason-badge">
            <div className="reason-icon">{config.icon}</div>
            <span className="reason-label">{config.label}</span>
            <Check size={14} className="reason-check" />
          </div>
        );
      })}
    </div>
  );
};

export const ProgressBar = ({ current, total }) => {
  const { language } = useLanguage();
  const percentage = (current / total) * 100;
  
  const getProgressText = () => {
    const texts = {
      de: `Frage ${current} von ${total}`,
      en: `Question ${current} of ${total}`,
      es: `Pregunta ${current} de ${total}`
    };
    return texts[language] || texts['de'];
  };
  
  return (
    <div className="progress-container">
      <div className="progress-info">
        <span className="progress-text">{getProgressText()}</span>
        <span className="progress-percentage">{Math.round(percentage)}%</span>
      </div>
      <div className="progress-bar-track">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
